import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Components
import Select from '../../../../components/atoms/select';
import Button from '../../../../components/atoms/button';
import Table, { TableColumn } from '../../../../components/organisms/table';
import Actions from '../../../../components/molecules/actions';
import Text from '../../../../components/atoms/text';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import TravelModal from './modal';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

// Services
import YardService, { IYard } from '../../../../services/Yard.service';
import ShipService, { IShip } from '../../../../services/Ship.service';
import MovementService, {
	IMovement,
} from '../../../../services/Movement.service';

// Utils & Types
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { dateObjToStr } from '../../../../utils/date';
import { ModalProps } from '../../../../components/template/mvc-modal';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

// ================ Constants & Types ================
interface TravelForm {
	yard: Partial<IYard>;
	ship: Partial<IShip>;
}

const DEFAULT_FORM: TravelForm = {
	yard: {},
	ship: {},
};

const INITIAL_MOVEMENT: IMovement = {
	travelCode: '',
	date: undefined,
	isArrived: false,
	owner: '',
	yard: {},
	ship: {},
	containers: [],
};

const DEFAULT_PAGINATION: PaginationProps = {
	pageCurrent: 1,
	pageSize: 10,
	totalItems: 0,
	totalPages: 1,
};

// ================ Main Component ================
const AuditoryTravel = () => {
	const { t } = useTranslation();
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	// ================ State ================
	const [yards, setYards] = useState<IYard[]>([]);
	const [ships, setShips] = useState<IShip[]>([]);
	const [form, setForm] = useState<TravelForm>(DEFAULT_FORM);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [movements, setMovements] = useState<IMovement[]>([]);
	const [pagination, setPagination] =
		useState<PaginationProps>(DEFAULT_PAGINATION);

	const [modalProps, setModalProps] = useState<ModalProps<IMovement>>(() => ({
		data: INITIAL_MOVEMENT,
		title: t('port_management.travels.view'),
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: INITIAL_MOVEMENT,
			}));
			if (reload) setSearchTrigger((prev) => !prev);
		},
	}));

	// ================ Memoized Values ================
	const modalTitles = useMemo(
		() => ({
			edit: t('port_management.travels.edit'),
			view: t('port_management.travels.view'),
		}),
		[t]
	);

	// ================ Table Columns ================
	const columns: TableColumn<IMovement>[] = useMemo(
		() => [
			{ key: 'travelCode', label: t('movement.travel_code') },
			{
				key: 'date',
				label: t('date.title'),
				children: (row) => (
					<Text className="text-xs">{dateObjToStr(row.date)}</Text>
				),
			},
			{
				key: 'actions',
				label: t('misc.actions'),
				children: (row) => (
					<Actions
						onViewClick={() => handleViewClick(row)}
						onEditClick={() => handleEditClick(row)}
					/>
				),
			},
		],
		[t]
	);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await YardService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setYards(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_yards'));
		}
	}, [accountId, t]);

	const fetchShips = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await ShipService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setShips(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_ships'));
		}
	}, [accountId, t]);

	const fetchMovements = useCallback(async () => {
		if (!form.yard.id && !form.ship.id) return;
		try {
			const response = await MovementService.getAll(
				{
					pageCurrent: pagination.pageCurrent,
					pageSize: pagination.pageSize,
					idClient: Number(accountId),
				},
				form.yard?.id,
				form.ship?.id
			);

			if (response) {
				setMovements(response.data);
				setPagination((prev) => ({
					...prev,
					totalItems: response.totalItems,
					totalPages: response.totalPages,
				}));
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_movements'));
		}
	}, [pagination.pageCurrent, pagination.pageSize, searchTrigger]);

	// ================ Effects ================
	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	useEffect(() => {
		fetchShips();
	}, [fetchShips]);

	useEffect(() => {
		fetchMovements();
	}, [fetchMovements]);

	// ================ Handlers ================
	const handleYardChange = useCallback(
		(e: EditableChangeEvent) => {
			const yard = yards.find((y) => y.name === e.target.value);
			if (!yard) return;

			setForm((prev) => ({ ...prev, yard }));
		},
		[yards]
	);

	const handleShipChange = useCallback(
		(e: EditableChangeEvent) => {
			const ship = ships.find((s) => s.name === e.target.value);
			if (!ship) return;

			setForm((prev) => ({ ...prev, ship }));
		},
		[ships]
	);

	const handleSearch = useCallback(() => {
		setSearchTrigger((prev) => !prev);
	}, []);

	const handleResetFilters = useCallback(() => {
		// Reset form to default values
		setForm(DEFAULT_FORM);
		// Trigger search to show all results
		setSearchTrigger((prev) => !prev);
		// Reset to first page
		setPagination((prev) => ({ ...prev, pageCurrent: 1 }));
	}, []);

	const handleViewClick = useCallback(
		(data: IMovement) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.view]
	);

	const handleEditClick = useCallback(
		(data: IMovement) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.edit]
	);

	const handlePaginationUpdate = useCallback(
		(pageSize: number, pageCurrent: number) => {
			setPagination((prev) => ({ ...prev, pageSize, pageCurrent }));
		},
		[]
	);

	// ================ Render Helpers ================
	const renderFilters = () => (
		<div className="flex gap-x-8 gap-y-4 flex-wrap items-end">
			<Select
				boxClassName="min-w-60"
				name="yard"
				value={form.yard.name || ''}
				onChange={handleYardChange}
				label={t('port_management.yard')}
				placeholder={t('port_management.yard')}
				options={yards.map((y) => y.name)}
				disabled={!accountId}
			/>

			<Select
				boxClassName="min-w-60"
				name="ship"
				value={form.ship.name || ''}
				onChange={handleShipChange}
				label={t('port_management.ship')}
				placeholder={t('port_management.ship')}
				options={ships.map((s) => s.name)}
				disabled={!accountId}
			/>

			<div className="flex gap-2">
				<Button onClick={handleSearch}>{t('misc.search')}</Button>

				<Button theme="light" onClick={handleResetFilters}>
					{t('misc.reset')}
				</Button>
			</div>
		</div>
	);

	const renderTable = () => (
		<div className="overflow-x-auto w-full flex flex-col gap-8">
			<Table
				data={movements}
				columns={columns}
				className={classNames('grid-cols-[1fr_1fr_auto]')}
			/>
		</div>
	);

	// ================ Render ================
	return (
		<>
			<div className="flex flex-col gap-4 px-8 py-4">
				{renderFilters()}
				{renderTable()}
				<Pagination {...pagination} onUpdate={handlePaginationUpdate} />
			</div>

			<TravelModal setModalProps={setModalProps} {...modalProps} />
		</>
	);
};

export default AuditoryTravel;
